import { useStyletron } from "baseui";
import { StyledListItem } from "baseui/menu";
import type { ComponentProps } from "react";
import React, { forwardRef } from "react";
import { expandBorderTop, marginHorizontal, paddingHorizontal, paddingVertical } from "../utils";

export type MenuListItemProps = ComponentProps<typeof StyledListItem> & {
  /** Whether this item is a header (non-clickable) */
  header?: boolean;
};

/**
 * A unified list item component for menus (MoreMenu, DropdownMenu, ContextMenu).
 *
 * Supports:
 * - Regular items with consistent padding/margin/borderRadius
 * - Dividers via `props.item.divider: true`
 * - Headers via `props.item.header: true` or `props.header: true`
 * - Disabled state via `props.$disabled: true`
 */
export const MenuListItem = forwardRef<HTMLLIElement, MenuListItemProps>((props, ref) => {
  const [_css, $theme] = useStyletron();
  const isHeader = props.item?.header || props.header;
  const isDivider = props.item?.divider;

  return (
    <StyledListItem
      ref={ref}
      {...props}
      $style={{
        ...(isDivider
          ? {
              ...expandBorderTop({
                borderColor: $theme.colors.borderOpaque,
                borderStyle: "solid",
                borderWidth: "1px",
              }),
              transform: `translateY(${$theme.sizing.scale200})`,
              ...paddingVertical($theme.sizing.scale200),
              height: 0,
              pointerEvents: "none",
            }
          : {
              ...(isHeader ? { cursor: "default" } : {}),
              ...(props.$disabled ? { color: $theme.colors.contentSecondary, filter: "grayscale(1)" } : {}),
              ...paddingHorizontal($theme.sizing.scale300),
              ...paddingVertical($theme.sizing.scale200),
              ...marginHorizontal($theme.sizing.scale200),
              borderRadius: $theme.sizing.scale200,
            }),
      }}
      ariaLabel={props.item?.ariaLabel ?? props["aria-label"]}
    />
  );
});
